if (!customElements.get('early-access-token')) {
  class EarlyAccessToken extends HTMLElement {
    static STORAGE_KEY = 'earlyAccessToken';
    static URL_PARAM = 'ea';
    static CART_ATTRIBUTE = 'early_access_token';
    static MAX_TOKEN_LENGTH = 100;
    static ACTIVE_CLASS = 'early-access-active';
    static LINE_PROPERTY_SELECTOR = 'input[name="properties[_early_access]"]';

    async connectedCallback() {
      const urlToken = this.readUrlToken();
      const storedToken = this.getStoredToken();
      const cartToken = (this.dataset.cartToken || '').trim();
      const openToken = (this.dataset.openToken || '').trim();

      let token = '';
      for (const candidate of [urlToken, storedToken, cartToken, openToken]) {
        if (candidate && (await this.isValidToken(candidate))) {
          token = candidate;
          break;
        }
      }

      if (storedToken && storedToken !== token) this.removeStoredToken();
      if (!token) return;
      if (token !== storedToken) this.storeToken(token);

      document.documentElement.classList.add(EarlyAccessToken.ACTIVE_CLASS);
      this.stampLineProperties(token);
      if (token !== (this.dataset.cartToken || '')) await this.applyToCart(token);
      this.markCartLines(token);
    }

    activeTokenHashes() {
      let schedule = [];
      try {
        schedule = JSON.parse(this.dataset.tokenSchedule || '[]');
      } catch (error) {
        console.warn('Early Access: could not read the discount schedule', error);
        return [];
      }

      const now = Date.now();
      return schedule
        .filter(({ startsAt, endsAt }) => {
          if (startsAt && Date.parse(startsAt) > now) return false;
          if (endsAt && Date.parse(endsAt) <= now) return false;
          return true;
        })
        .map(({ hash }) => hash);
    }

    async isValidToken(token) {
      const hashes = this.activeTokenHashes();
      if (!hashes.length || !window.crypto || !window.crypto.subtle) return false;

      try {
        const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(token));
        const hash = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
        return hashes.includes(hash);
      } catch (error) {
        console.warn('Early Access: could not check the token', error);
        return false;
      }
    }

    stampLineProperties(token) {
      const stamp = () => {
        document.querySelectorAll(EarlyAccessToken.LINE_PROPERTY_SELECTOR).forEach((input) => {
          input.value = token;
        });
      };

      stamp();
      document.addEventListener('click', stamp, true);
      document.addEventListener('submit', stamp, true);
    }

    readUrlToken() {
      try {
        const url = new URL(window.location.href);
        const token = (url.searchParams.get(EarlyAccessToken.URL_PARAM) || '').trim();
        if (!token || token.length > EarlyAccessToken.MAX_TOKEN_LENGTH) return '';

        url.searchParams.delete(EarlyAccessToken.URL_PARAM);
        window.history.replaceState(window.history.state, '', url.toString());
        return token;
      } catch (error) {
        console.warn('Early Access: could not read token from URL', error);
        return '';
      }
    }

    getStoredToken() {
      if (typeof StorageUtility === 'undefined') return '';
      return StorageUtility.get(EarlyAccessToken.STORAGE_KEY, '') || '';
    }

    removeStoredToken() {
      if (typeof StorageUtility === 'undefined') return;
      StorageUtility.remove(EarlyAccessToken.STORAGE_KEY);
    }

    storeToken(token) {
      if (typeof StorageUtility === 'undefined') return;
      StorageUtility.set(EarlyAccessToken.STORAGE_KEY, token);
    }

    async applyToCart(token) {
      try {
        const updateUrl = (window.routes && window.routes.cart_update_url) || '/cart/update';
        const response = await fetch(`${updateUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { [EarlyAccessToken.CART_ATTRIBUTE]: token } }),
        });
        if (!response.ok) throw new Error(`cart update failed (${response.status})`);
        this.dataset.cartToken = token;
      } catch (error) {
        console.error('Early Access: could not save token to cart', error);
      }
    }

    async markCartLines(token) {
      const variantIds = (this.dataset.unmarkedLines || '').split(',').filter(Boolean);
      if (!variantIds.length) return;

      const routes = window.routes || {};
      const cartUrl = routes.cart_url || '/cart';
      const changeUrl = routes.cart_change_url || '/cart/change';

      try {
        const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) throw new Error(`cart read failed (${response.status})`);
        const cart = await response.json();
        const unmarked = (cart.items || []).filter(
          (item) => variantIds.includes(String(item.variant_id)) && (item.properties || {})._early_access !== token
        );

        for (const item of unmarked) {
          const current = await (await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } })).json();
          const line = (current.items || []).find(
            (currentItem) =>
              currentItem.variant_id === item.variant_id && (currentItem.properties || {})._early_access !== token
          );
          if (!line) continue;
          const changeResponse = await fetch(`${changeUrl}.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              id: line.key,
              quantity: line.quantity,
              properties: { ...(line.properties || {}), _early_access: token },
            }),
          });
          if (!changeResponse.ok) throw new Error(`cart line update failed (${changeResponse.status})`);
        }
      } catch (error) {
        console.warn('Early Access: could not mark existing cart lines', error);
      }
    }
  }

  customElements.define('early-access-token', EarlyAccessToken);
}
